<app-split-pane
  storageKey="split.define"
  [initialLeftRatio]="0.7"
  [collapseBreakpointPx]="1024"
  [collapsedOnSmallScreens]="true"
>
  <ng-container left>
    @if (!ticketsStore.activeProjectId() || ticketsStore.loading()) {
      <app-split-pane-vertical storageKey="split.tickets.left" [initialTopRatio]="0.7">
        <ng-container top>
          <div class="p-3">
            <div class="card border-0 shadow-sm p-3">
              <app-skeleton-block height="14px" width="42%" rounded="8px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block [lines]="6" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block height="44px" width="100%" rounded="10px"></app-skeleton-block>
            </div>
          </div>
        </ng-container>
        <ng-container bottom>
          <div class="p-3">
            <div class="card border-0 shadow-sm p-3">
              <app-skeleton-block height="14px" width="30%" rounded="8px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block [lines]="5" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
            </div>
          </div>
        </ng-container>
      </app-split-pane-vertical>
    } @else {
      <app-split-pane-vertical storageKey="split.tickets.left" [initialTopRatio]="0.7">
        <ng-container top>
          <app-chat
            #ticketsChat
            [conversationId]="ticketsStore.activeProjectId()"
            [suggestions]="ticketsSuggestions"
            [context]="ticketsContext"
            [modelSurfaceKey]="'chat.tickets'"
            [orchestratorPageContext]="ticketsOrchestratorContext"
            [initialMessages]="ticketsStore.chatMessages()"
            [disabled]="!definitionReady"
            (messagesChange)="onChatMessagesChange($event)"
            (messageCreated)="onChatMessageCreated($event)"
            (aiResult)="onAiResult($event)"
            placeholder="Describe your tickets..."
          ></app-chat>
        </ng-container>
        <ng-container bottom>
          <app-history scopeKey="tickets.chat"></app-history>
        </ng-container>
      </app-split-pane-vertical>
    }
  </ng-container>

  <ng-container right>
    @if (!ticketsStore.activeProjectId() || ticketsStore.loading()) {
      <app-split-pane-vertical storageKey="split.tickets.right" [initialTopRatio]="0.7">
        <ng-container top>
          <div class="p-3">
            <div class="card border-0 shadow-sm p-3">
              <app-skeleton-block height="14px" width="28%" rounded="8px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block height="220px" width="100%" rounded="12px"></app-skeleton-block>
            </div>
          </div>
        </ng-container>
        <ng-container bottom>
          <div class="p-3">
            <div class="card border-0 shadow-sm p-3">
              <app-skeleton-block height="14px" width="26%" rounded="8px"></app-skeleton-block>
              <div class="mt-3"></div>
              <app-skeleton-block [lines]="5" height="12px" width="100%" rounded="8px" gap="10px"></app-skeleton-block>
            </div>
          </div>
        </ng-container>
      </app-split-pane-vertical>
    } @else {
      <app-split-pane-vertical storageKey="split.tickets.right" [initialTopRatio]="0.7">
        <ng-container top>
          <div class="tickets-board-wrap">
            @if (boardReady) {
              <app-kanban-board
                [storageKey]="null"
                [initialColumns]="ticketsStore.board()"
                [agentifyEnabled]="canAgentifyFeedbackTickets"
                [agentifyingCardIds]="agentifyingCardIds"
                [resumableCardIds]="resumableExecuteCardIds"
                (boardChange)="onBoardChange($event)"
                (boardCommit)="onBoardCommit($event)"
                (cardSelected)="onCardSelected($event)"
                (cardDropped)="onCardDropped($event)"
                (agentifyCard)="onAgentifyCard($event)"
                (resumeCard)="onResumeCard($event)"
              >
                <div kanban-header-actions class="tickets-model-selector">
                  <label for="tickets-exec-model" class="small text-muted">Execution agent</label>
                  <select id="tickets-exec-model" class="form-select form-select-sm" [(ngModel)]="execModelId">
                    @for (m of execModelOptions; track m.id) {
                      <option [value]="m.id">{{ m.label }}</option>
                    }
                  </select>
                </div>
              </app-kanban-board>
            } @else {
              <div class="p-3 d-flex align-items-center gap-2 text-muted small">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Loading board…
              </div>
            }

            <ng-template #executeModal let-modal>
              <div class="modal-header">
                <h5 class="modal-title">Execute ticket</h5>
                <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss('close')"></button>
              </div>
              <div class="modal-body">
                @if (!pendingExecution) {
                  <div class="text-muted small">No ticket selected.</div>
                } @else {
                  <div class="small">
                    <div class="mb-2">
                      <div class="fw-semibold">Ticket</div>
                      <div><code>{{ pendingExecution.ticket.id }}</code> — {{ pendingExecution.ticket.title }}</div>
                    </div>
                    <div class="mb-2">
                      <div class="fw-semibold">Target repo</div>
                      <div><code>{{ pendingExecution.owner }}/{{ pendingExecution.repo }}</code></div>
                      <div class="small text-muted mt-1">
                        Routing confidence: <strong>{{ (pendingExecution.routingConfidence * 100) | number:'1.0-0' }}%</strong>
                      </div>
                      <div class="small text-muted">{{ pendingExecution.routingReason }}</div>
                      @if (pendingExecution.routingNeedsConfirmation) {
                        <div class="alert alert-warning py-2 px-3 mt-2 mb-0 small">
                          Repo routing is ambiguous. Please confirm the target repository before executing.
                        </div>
                      }
                    </div>
                    <div class="mb-2">
                      <label class="fw-semibold d-block mb-1">Confirm target repo</label>
                      <select
                        class="form-select form-select-sm"
                        [ngModel]="pendingExecution.targetKey"
                        (ngModelChange)="onPendingTargetRepoChanged($event)"
                        [disabled]="execBusy"
                      >
                        <option value="">Select a repository</option>
                        @for (r of pendingExecution.repoChoices; track r.owner + '/' + r.repo) {
                          <option [value]="r.owner + '/' + r.repo">{{ r.owner }}/{{ r.repo }} ({{ r.role }})</option>
                        }
                      </select>
                    </div>
                    <div class="mb-2">
                      <label class="fw-semibold d-block mb-1">Ticket scope (repo role)</label>
                      <select class="form-select form-select-sm" [(ngModel)]="pendingExecution.ticket.repoRole" [disabled]="execBusy">
                        @for (role of repoRoleOptions; track role.id) {
                          <option [value]="role.id">{{ role.label }}</option>
                        }
                      </select>
                    </div>
                    <div class="mb-2">
                      <label class="fw-semibold d-block mb-1">Risk</label>
                      <select class="form-select form-select-sm" [(ngModel)]="pendingExecution.ticket.risk" [disabled]="execBusy">
                        <option value="">Select risk</option>
                        @for (r of riskOptions; track r.id) {
                          <option [value]="r.id">{{ r.label }}</option>
                        }
                      </select>
                    </div>
                    <div class="mb-2">
                      <label class="fw-semibold d-block mb-1">Acceptance criteria (one per line)</label>
                      <textarea
                        class="form-control form-control-sm"
                        rows="4"
                        [ngModel]="(pendingExecution.ticket.acceptanceCriteria ?? []).join('\n')"
                        (ngModelChange)="onPendingAcceptanceCriteriaChange($event)"
                        [disabled]="execBusy"
                        placeholder="- User can ...&#10;- API returns ..."
                      ></textarea>
                    </div>
                    <div class="mb-2">
                      <label class="fw-semibold d-block mb-1">Base branch</label>
                      <input class="form-control form-control-sm exec-base-branch" [value]="'dev'" readonly />
                      <div class="small text-muted mt-1">Policy enforced: ticket execution targets <code>dev</code> only.</div>
                    </div>
                    <div class="mb-2">
                      <label class="fw-semibold d-block mb-1">Execution model</label>
                      <select class="form-select form-select-sm" [(ngModel)]="execModelId" [disabled]="execBusy">
                        @for (m of execModelOptions; track m.id) {
                          <option [value]="m.id">{{ m.label }}</option>
                        }
                      </select>
                      <div class="small text-muted mt-1">Used only for ticket implementation on execute.</div>
                    </div>
                    <div class="text-muted">This will create a branch, commit code changes, and open a PR.</div>
                  </div>
                }
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary" (click)="onExecuteModalCancel(modal)" [disabled]="execBusy">Cancel</button>
                <button type="button" class="btn btn-primary" (click)="onExecuteModalConfirm(modal)" [disabled]="execBusy || !pendingExecution">
                  @if (execBusy) {
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Executing…
                  } @else {
                    Execute
                  }
                </button>
              </div>
            </ng-template>
          </div>
        </ng-container>
        <ng-container bottom>
          <app-history scopeKey="tickets.board"></app-history>
        </ng-container>
      </app-split-pane-vertical>
    }
  </ng-container>
</app-split-pane>
